/**
 * SetupChecklistBanner
 * ====================
 * One-time setup nudge that appears on the Dashboard when the org is missing
 * either Company Info or Pricing Profile — both required for the auto-fill
 * "magic" to feel intelligent (not generic). Dismisses itself once both
 * checkpoints are filled, so it never nags a fully-onboarded user.
 *
 * Why this exists: a brand-new account can sign up and create quotes
 * immediately, but the PDF would show blank company info and the cost auto-fill
 * would use generic per-square defaults. That's the opposite of "intelligent."
 * This banner gets new users to the magic state in <2 minutes.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle2, Circle, ChevronRight, X, Sparkles } from 'lucide-react';
import api from '@/lib/api';

const DISMISSED_KEY = 'qf-setup-banner-dismissed';

export function SetupChecklistBanner() {
    const [state, setState] = useState({ loading: true, items: [], pctDone: 0 });
    const [dismissed, setDismissed] = useState(() => {
        try { return localStorage.getItem(DISMISSED_KEY) === '1'; } catch { return false; }
    });

    useEffect(() => {
        let cancelled = false;
        async function load() {
            try {
                // Pull org + pricing profile in parallel. /auth/me returns the org doc;
                // /me/pricing-profile returns the merged profile (with `_is_default` flag
                // indicating the contractor hasn't customized it yet).
                const [meR, profR] = await Promise.all([
                    api.get('/auth/me'),
                    api.get('/me/pricing-profile').catch(() => null),
                ]);
                if (cancelled) return;
                // Org settings is the source of truth for the "company info" that
                // gets stamped on every PDF. (Each quote stores a snapshot in
                // `quote.company_info` at create-time; org.settings is the master.)
                const settings = meR.data?.org?.settings || {};
                const hasCompanyName = !!settings.legal_name;
                const hasLicense = !!(settings.license_number && settings.license_state);
                const hasInsurance = !!settings.liability_insurance;
                const hasContact = !!(settings.phone || settings.email);
                const hasCustomPricing = !!(profR?.data?.has_custom);

                const items = [
                    { key: 'company_name', label: 'Company name on the PDF', done: hasCompanyName, target: '/app/settings' },
                    { key: 'contact',      label: 'Phone or email for customers',    done: hasContact,      target: '/app/settings' },
                    { key: 'license',      label: 'Contractor license # + state',    done: hasLicense,      target: '/app/settings' },
                    { key: 'insurance',    label: 'Liability insurance carrier',     done: hasInsurance,    target: '/app/settings' },
                    { key: 'pricing',      label: 'Pricing profile (your labor + material costs)', done: hasCustomPricing, target: '/app/settings#pricing-profile' },
                ];
                const doneCount = items.filter(i => i.done).length;
                const pctDone = Math.round((doneCount / items.length) * 100);
                setState({ loading: false, items, pctDone });
            } catch (_e) {
                if (!cancelled) setState({ loading: false, items: [], pctDone: 0 });
            }
        }
        load();
        return () => { cancelled = true; };
    }, []);

    const allDone = state.items.length > 0 && state.items.every(i => i.done);

    // Hide once fully set up. Also hide if user explicitly dismissed.
    if (state.loading || allDone || dismissed) return null;

    const firstIncomplete = state.items.find(i => !i.done);

    return (
        <Card
            className="border-amber-200 bg-gradient-to-br from-amber-50 to-white shadow-sm"
            data-testid="setup-checklist-banner"
        >
            <CardContent className="p-4 sm:p-5">
                <div className="flex items-start gap-4">
                    <div className="hidden sm:flex h-10 w-10 rounded-full bg-amber-500 items-center justify-center shrink-0">
                        <Sparkles className="h-5 w-5 text-slate-950" strokeWidth={2.5} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                            <div className="font-display font-semibold text-slate-900">
                                Finish setting up to unlock the magic
                            </div>
                            <button
                                onClick={() => { try { localStorage.setItem(DISMISSED_KEY, '1'); } catch {} setDismissed(true); }}
                                aria-label="Dismiss"
                                className="text-slate-400 hover:text-slate-600 shrink-0"
                                data-testid="setup-banner-dismiss"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                        <p className="text-sm text-slate-600 mt-0.5">
                            {state.pctDone}% complete &mdash; without these, your quotes use generic defaults instead of your business&apos;s actual numbers.
                        </p>

                        {/* Progress bar */}
                        <div className="mt-2 h-1.5 rounded-full bg-amber-100 overflow-hidden">
                            <div
                                className="h-full bg-amber-500 transition-all"
                                style={{ width: `${state.pctDone}%` }}
                            />
                        </div>

                        {/* Itemized checklist */}
                        <ul className="mt-3 space-y-1.5" data-testid="setup-checklist-items">
                            {state.items.map(item => (
                                <li
                                    key={item.key}
                                    className="flex items-center justify-between gap-3 text-sm"
                                    data-testid={`setup-item-${item.key}`}
                                >
                                    <div className="flex items-center gap-2 min-w-0">
                                        {item.done
                                            ? <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
                                            : <Circle className="h-4 w-4 text-slate-300 shrink-0" />}
                                        <span className={item.done ? 'text-slate-500 line-through' : 'text-slate-900'}>
                                            {item.label}
                                        </span>
                                    </div>
                                    {!item.done && (
                                        <Link
                                            to={item.target}
                                            className="text-xs text-amber-700 hover:text-amber-800 font-medium inline-flex items-center gap-0.5 shrink-0"
                                            data-testid={`setup-item-${item.key}-link`}
                                        >
                                            Set up <ChevronRight className="h-3 w-3" />
                                        </Link>
                                    )}
                                </li>
                            ))}
                        </ul>

                        {firstIncomplete && (
                            <div className="mt-3">
                                <Button
                                    asChild
                                    size="sm"
                                    className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold"
                                    data-testid="setup-banner-cta"
                                >
                                    <Link to={firstIncomplete.target}>
                                        Set up: {firstIncomplete.label} <ChevronRight className="h-4 w-4 ml-1" />
                                    </Link>
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}
